/**
 * Deposits sidebar component for History section
 */
import {
  Add,
  ChevronLeft,
  ChevronRight,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { fieldsApi, type FieldListParams } from '@/api/fields';
import { SIDEBAR_WIDTH } from '@/components/Layout/Sidebar';
import { useHistory } from '@/contexts/HistoryContext';

const DEPOSITS_SIDEBAR_WIDTH = 280;
const DEPOSITS_SIDEBAR_COLLAPSED_WIDTH = 64;
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

type VisibilityFilter = NonNullable<FieldListParams['visibility_status']>;

interface Deposit {
  id: string;
  name: string;
}

interface DepositsSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mainSidebarCollapsed?: boolean;
  /** Mobile: temporary drawer (controlled by open / onClose) */
  variant?: 'permanent' | 'temporary';
  open?: boolean;
  onClose?: () => void;
}

export const DepositsSidebar: React.FC<DepositsSidebarProps> = ({
  collapsed = false,
  onToggleCollapse,
  mainSidebarCollapsed = false,
  variant = 'permanent',
  open: temporaryOpen = false,
  onClose,
}) => {
  const isTemporary = variant === 'temporary';
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedDepositId, setSelectedDepositId, fieldsVersion, openCreateFieldForm } = useHistory();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [page, setPage] = useState(1);
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initialUrlFieldIdRef = useRef(searchParams.get('fieldId'));
  const autoSelectDoneRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const loadDeposits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fieldsApi.getFieldsList({
        search: debouncedSearch || undefined,
        visibility_status: visibilityFilter,
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      const depositsData: Deposit[] = response.fields.map((field) => ({
        id: field.id,
        name: field.name,
      }));
      setDeposits(depositsData);
      setTotal(response.total);

      const urlFieldId = initialUrlFieldIdRef.current;
      setSelectedDepositId((current) => {
        if (current) {
          return current;
        }
        if (urlFieldId) {
          return urlFieldId;
        }
        if (!autoSelectDoneRef.current) {
          autoSelectDoneRef.current = true;
          return depositsData[0]?.id ?? null;
        }
        return current;
      });
    } catch (err) {
      console.error('Failed to load deposits:', err);
      setError('Failed to load deposits');
      setDeposits([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, visibilityFilter, page, setSelectedDepositId]);

  useEffect(() => {
    void loadDeposits();
  }, [fieldsVersion, loadDeposits]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleVisibilityChange = (value: VisibilityFilter) => {
    setVisibilityFilter(value);
    setPage(1);
  };

  const handleSelectDeposit = (depositId: string) => {
    setSelectedDepositId(depositId);
    setSearchParams({ fieldId: depositId }, { replace: true });
    if (isTemporary) {
      onClose?.();
    }
  };

  return (
    <Drawer
      variant={isTemporary ? 'temporary' : 'permanent'}
      open={isTemporary ? temporaryOpen : undefined}
      onClose={isTemporary ? onClose : undefined}
      ModalProps={isTemporary ? { keepMounted: true } : undefined}
      sx={{
        width: isTemporary ? undefined : (collapsed ? DEPOSITS_SIDEBAR_COLLAPSED_WIDTH : DEPOSITS_SIDEBAR_WIDTH),
        flexShrink: 0,
        transition: 'width 0.3s ease',
        zIndex: isTemporary ? (theme) => theme.zIndex.drawer + 1 : undefined,
        '& .MuiDrawer-paper': {
          width: collapsed ? DEPOSITS_SIDEBAR_COLLAPSED_WIDTH : DEPOSITS_SIDEBAR_WIDTH,
          maxWidth: '100vw',
          boxSizing: 'border-box',
          borderRight: 1,
          borderColor: 'divider',
          position: isTemporary ? undefined : 'relative',
          height: isTemporary ? '100dvh' : '100%',
          ...(isTemporary && {
            display: 'flex',
            flexDirection: 'column',
          }),
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{
        ...(isTemporary
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : { height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', pb: 8 }),
      }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: collapsed ? 0 : 2 }}>
            {!collapsed && (
              <Typography variant="h6" color="primary" fontWeight="bold">
                Deposits
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Add field">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => openCreateFieldForm()}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {!collapsed && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search deposits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl fullWidth size="small">
                <InputLabel id="deposit-visibility-filter-label">Visibility</InputLabel>
                <Select
                  labelId="deposit-visibility-filter-label"
                  label="Visibility"
                  value={visibilityFilter}
                  onChange={(e) => handleVisibilityChange(e.target.value as VisibilityFilter)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="company_public">Company public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading deposits...
              </Typography>
            </Box>
          )}

          {error && !loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            </Box>
          )}

          {!loading && deposits.length > 0 && (
            <List sx={{ py: 1 }}>
              {deposits.map((deposit) => (
                <ListItem key={deposit.id} disablePadding>
                  <Tooltip
                    title={collapsed ? deposit.name : ''}
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      selected={selectedDepositId === deposit.id}
                      onClick={() => handleSelectDeposit(deposit.id)}
                      sx={{
                        mx: 1,
                        borderRadius: 1,
                        mb: 0.5,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      }}
                    >
                      {!collapsed && (
                        <ListItemText
                          primary={deposit.name}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: selectedDepositId === deposit.id ? 600 : 400,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          )}

          {!loading && !error && total === 0 && !debouncedSearch && visibilityFilter === 'all' && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No fields yet. Create your first field.
              </Typography>
              {!collapsed && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => openCreateFieldForm()}
                >
                  Add field
                </Button>
              )}
            </Box>
          )}

          {!loading && !error && total === 0 && (debouncedSearch || visibilityFilter !== 'all') && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No deposits found
              </Typography>
            </Box>
          )}
        </Box>

        {!collapsed && total > PAGE_SIZE && (
          <Box
            sx={{
              px: 1,
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Pagination
              count={pageCount}
              page={Math.min(page, pageCount)}
              onChange={(_, value) => setPage(value)}
              size="small"
              color="primary"
              siblingCount={0}
              boundaryCount={1}
            />
            <Typography variant="caption" color="text.secondary">
              {total} deposit{total === 1 ? '' : 's'}
            </Typography>
          </Box>
        )}

        {!isTemporary && onToggleCollapse && (
          <Box sx={{
            position: 'fixed',
            bottom: 16,
            left: (mainSidebarCollapsed ? 64 : SIDEBAR_WIDTH) + 16,
            width: collapsed ? DEPOSITS_SIDEBAR_COLLAPSED_WIDTH - 32 : DEPOSITS_SIDEBAR_WIDTH - 32,
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              sx={{
                color: 'primary.main',
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.dark',
                },
              }}
              title={collapsed ? 'Expand deposits sidebar' : 'Collapse deposits sidebar'}
            >
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export { DEPOSITS_SIDEBAR_COLLAPSED_WIDTH, DEPOSITS_SIDEBAR_WIDTH };
